/**
 * Construction kit shared by every team building: foundations, doorways, windows,
 * roof hardware, site furniture, and the damage language for ruined states
 * (scorch, rubble, bent posts, exposed frame). Raising the baseline here raises
 * all ten buildings at once; each team builder only adds its signature pieces.
 *
 * Units are building-local (createStructureMesh scales the group ×2).
 */

import * as THREE from 'three';
import type { TeamPalette } from '../types';
import { REFERENCE } from '../constants';

// ---------------------------------------------------------------------------
// materials + primitives
// ---------------------------------------------------------------------------
export const KIT = {
  darkMetal: '#3a3c44',
  lightMetal: '#b8bcc4',
  concrete: '#8a8a84',
  glassDark: '#1c222c',
  solar: '#243454',
  scorch: '#1a1410',
  rubbleDark: '#4a4440',
};

export const mat = (c: string) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c) });
// One emissive intensity for the whole kit: every glowing part of a building then shares a single baked
// draw call (bakeStaticParts buckets by emissive colour + intensity). The parameter is kept for call-site readability.
export const matE = (c: string, _i = 0.5) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c), emissive: new THREE.Color(c), emissiveIntensity: 0.5 });
export function matGlass(c: string, opacity = 0.55): THREE.MeshLambertMaterial {
  const m = mat(c); m.transparent = true; m.opacity = opacity; m.side = THREE.DoubleSide; return m;
}
export const box = (w: number, h: number, d: number, m: THREE.Material) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
export const cyl = (rt: number, rb: number, h: number, m: THREE.Material, seg = 12) => new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
export const sphere = (r: number, m: THREE.Material, ws = 12, hs = 8) => new THREE.Mesh(new THREE.SphereGeometry(r, ws, hs), m);
export const torus = (r: number, t: number, m: THREE.Material, rs = 6, ts = 16, arc = Math.PI * 2) => new THREE.Mesh(new THREE.TorusGeometry(r, t, rs, ts, arc), m);
export function at<T extends THREE.Object3D>(o: T, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): T {
  o.position.set(x, y, z); o.rotation.set(rx, ry, rz); return o;
}
export function group(...children: THREE.Object3D[]): THREE.Group { const g = new THREE.Group(); g.add(...children); return g; }

/** Tiny deterministic generator for rubble scatter (same team → same debris). */
export class KitRng {
  private s: number;
  constructor(seed: number) { this.s = (seed * 2654435761 + 12345) >>> 0 || 1; }
  next(): number { this.s ^= this.s << 13; this.s >>>= 0; this.s ^= this.s >>> 17; this.s ^= this.s << 5; this.s >>>= 0; return this.s / 4294967296; }
  range(a: number, b: number): number { return a + (b - a) * this.next(); }
}

// ---------------------------------------------------------------------------
// foundations + shell parts
// ---------------------------------------------------------------------------

/** Foundation slab with a lighter top plate and four corner footings. */
export function pad(t: TeamPalette, w: number, d: number, h = 0.05, ruined = false): THREE.Group {
  const g = group(
    at(box(w, h, d, mat(KIT.concrete)), 0, h / 2, 0),
    at(box(w * 0.96, 0.012, d * 0.96, mat(ruined ? '#2a2420' : t.accent)), 0, h + 0.006, 0),   // ruined: the slab itself is scorched
  );
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) g.add(at(box(0.06, h + 0.02, 0.06, mat(KIT.darkMetal)), sx * (w / 2 - 0.04), (h + 0.02) / 2, sz * (d / 2 - 0.04)));
  return g;
}

/** Door with frame, a step and a lamp above. `ruined` lays the panel flat in front. */
export function doorway(t: TeamPalette, ruined: boolean, w = 0.14, h = 0.22): THREE.Group {
  const g = new THREE.Group();
  g.add(at(box(w + 0.04, h + 0.03, 0.025, mat(t.accent)), 0, h / 2 + 0.015, -0.005));         // frame
  g.add(at(box(w + 0.1, 0.03, 0.08, mat(KIT.concrete)), 0, 0.015, 0.05));                     // step
  if (ruined) {
    g.add(at(box(w, h, 0.015, mat(t.detail)), 0.06, 0.008, 0.2, Math.PI / 2 - 0.08, 0.5, 0)); // panel blown off, lying ahead
    g.add(at(box(w - 0.02, h - 0.02, 0.01, mat(KIT.glassDark)), 0, h / 2 + 0.01, 0.0));        // dark opening
  } else {
    g.add(at(box(w, h, 0.015, mat(t.detail)), 0, h / 2, 0.006));                              // panel
    g.add(at(box(0.03, 0.06, 0.008, mat(t.accent)), w / 2 - 0.03, h / 2, 0.016));             // handle plate
    g.add(at(box(0.05, 0.02, 0.02, matE(t.visor, 0.6)), 0, h + 0.06, 0.012));                 // lamp
  }
  return g;
}

/** Window with a frame; glass glows when intact, goes dark when ruined. */
export function windowPanel(t: TeamPalette, ruined: boolean, w: number, h: number): THREE.Group {
  return group(
    at(box(w + 0.03, h + 0.03, 0.02, mat(t.accent)), 0, 0, -0.004),
    at(box(w, h, 0.012, ruined ? mat(KIT.glassDark) : matE(t.visor, 0.35)), 0, 0, 0.006),
    ...(ruined ? [at(box(w * 0.7, 0.006, 0.014, mat(KIT.lightMetal)), 0, 0, 0.007, 0, 0, 0.5)] : []), // crack
  );
}

/** Roof HVAC unit: box, grille slats, a short pipe. */
export function roofUnit(t: TeamPalette, size = 0.14): THREE.Group {
  const g = group(at(box(size, size * 0.55, size, mat(t.detail)), 0, size * 0.275, 0));
  for (let i = -1; i <= 1; i++) g.add(at(box(size * 0.8, 0.008, 0.012, mat(KIT.darkMetal)), 0, size * 0.275 + i * size * 0.14, size / 2 + 0.004));
  g.add(at(cyl(0.015, 0.015, size * 0.5, mat(KIT.lightMetal), 8), size * 0.3, size * 0.55 + size * 0.2, -size * 0.2));
  return g;
}

/** Pipe run with elbows and wall brackets, along local +X. */
export function pipeRun(len: number, r = 0.012): THREE.Group {
  const g = group(at(cyl(r, r, len, mat(KIT.lightMetal), 8), 0, 0, 0, 0, 0, Math.PI / 2));
  g.add(at(sphere(r * 1.3, mat(KIT.lightMetal), 8, 6), -len / 2, 0, 0), at(sphere(r * 1.3, mat(KIT.lightMetal), 8, 6), len / 2, 0, 0));
  for (const x of [-len / 4, len / 4]) g.add(at(box(0.02, 0.03, 0.02, mat(KIT.darkMetal)), x, -0.012, 0));
  return g;
}

/** Vent stack with cap. */
export function vent(h = 0.1): THREE.Group {
  return group(at(cyl(0.02, 0.022, h, mat(KIT.lightMetal), 8), 0, h / 2, 0), at(cyl(0.035, 0.03, 0.015, mat(KIT.darkMetal), 8), 0, h + 0.01, 0));
}

/** Solar panel on a post, tilted toward +Z. `ruined` tips it over. */
export function solarPanel(t: TeamPalette, ruined: boolean): THREE.Group {
  const g = new THREE.Group();
  const post = at(cyl(0.015, 0.018, 0.16, mat(KIT.darkMetal), 6), 0, 0.08, 0);
  const panel = group(
    at(box(0.22, 0.012, 0.16, mat(t.accent)), 0, 0, 0),
    at(box(0.2, 0.006, 0.14, mat(KIT.solar)), 0, 0.008, 0),
    at(box(0.004, 0.008, 0.14, mat(KIT.lightMetal)), -0.05, 0.009, 0), at(box(0.004, 0.008, 0.14, mat(KIT.lightMetal)), 0.05, 0.009, 0),
  );
  if (ruined) { at(post, 0.04, 0.05, 0, 0, 0, 1.1); at(panel, 0.14, 0.03, 0.05, 0.25, 0.4, 0.35); }   // panel knocked down, lying askew
  else at(panel, 0, 0.17, 0, -0.45);
  g.add(post, panel);
  return g;
}

/** Site light pole; the head glows when intact. */
export function lightPole(t: TeamPalette, ruined: boolean, h = 0.42): THREE.Group {
  const g = group(
    at(cyl(0.012, 0.016, h, mat(KIT.darkMetal), 6), 0, h / 2, 0),
    at(box(0.08, 0.025, 0.05, mat(t.accent)), 0.03, h + 0.01, 0),
    at(box(0.06, 0.008, 0.035, ruined ? mat(KIT.glassDark) : matE(t.visor, 0.7)), 0.03, h - 0.004, 0),
  );
  if (ruined) g.rotation.z = 0.75;
  return g;
}

/** Team sign on a pole: primary board, detail stripe, two mounting bolts. */
export function signPost(t: TeamPalette, ruined: boolean, h = 0.34): THREE.Group {
  const g = group(
    at(cyl(0.012, 0.012, h, mat(KIT.darkMetal), 6), 0, h / 2, 0),
    at(box(0.2, 0.1, 0.015, mat(t.primary)), 0, h + 0.02, 0),
    at(box(0.2, 0.025, 0.017, mat(t.detail)), 0, h - 0.02, 0),
    at(box(0.12, 0.02, 0.018, mat(t.visor)), 0, h + 0.035, 0),
  );
  if (ruined) g.rotation.z = -0.9;
  return g;
}

export function crate(t: TeamPalette, s = 0.12): THREE.Group {
  return group(at(box(s, s, s, mat(t.accent)), 0, s / 2, 0), at(box(s * 1.02, 0.012, 0.012, mat(KIT.darkMetal)), 0, s / 2, 0), at(box(0.012, s * 1.02, 0.012, mat(KIT.darkMetal)), 0, s / 2, s / 2));
}
export function barrel(c: string, r = 0.06, h = 0.16): THREE.Group {
  return group(at(cyl(r, r, h, mat(c), 12), 0, h / 2, 0), at(torus(r, 0.008, mat(KIT.darkMetal), 6, 16), 0, h * 0.3, 0, Math.PI / 2), at(torus(r, 0.008, mat(KIT.darkMetal), 6, 16), 0, h * 0.7, 0, Math.PI / 2));
}
export function pallet(w = 0.22, d = 0.16): THREE.Group {
  const g = new THREE.Group();
  for (let i = -1; i <= 1; i++) g.add(at(box(w, 0.012, 0.03, mat('#9a8460')), 0, 0.03, i * d * 0.4));
  for (const x of [-w / 2 + 0.02, 0, w / 2 - 0.02]) g.add(at(box(0.03, 0.024, d, mat('#86704f')), x, 0.012, 0));
  return g;
}
export function ladder(h: number): THREE.Group {
  const g = group(at(box(0.012, h, 0.012, mat(KIT.lightMetal)), -0.04, h / 2, 0), at(box(0.012, h, 0.012, mat(KIT.lightMetal)), 0.04, h / 2, 0));
  for (let y = 0.04; y < h; y += 0.06) g.add(at(box(0.09, 0.01, 0.01, mat(KIT.lightMetal)), 0, y, 0));
  return g;
}
/** Antenna mast on a tripod with a dish (feed arm included). `ruined` lays it down. */
export function dishMast(t: TeamPalette, ruined: boolean, h = 0.5, dishR = 0.11): THREE.Group {
  const g = new THREE.Group();
  for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; g.add(at(cyl(0.006, 0.006, 0.12, mat(KIT.darkMetal), 4), Math.cos(a) * 0.04, 0.055, Math.sin(a) * 0.04, Math.sin(a) * 0.5, 0, -Math.cos(a) * 0.5)); }
  const mast = at(cyl(0.012, 0.014, h, mat(t.detail), 6), 0, h / 2, 0);
  const dish = group(
    at(new THREE.Mesh(new THREE.SphereGeometry(dishR, 14, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), mat(t.visor)), 0, 0, 0, Math.PI),
    at(cyl(0.005, 0.005, dishR * 0.7, mat(KIT.lightMetal), 4), 0, -dishR * 0.35, 0),
    at(sphere(0.012, matE(t.visor, 0.5), 6, 6), 0, -dishR * 0.72, 0),
  );
  if (ruined) { at(mast, 0.12, 0.08, 0.1, 0, 0, 1.35); at(dish, 0.38, 0.05, 0.14, 0.6, 0.4, 1.9); }
  else at(dish, 0, h + 0.02, 0, -0.6);
  g.add(mast, dish);
  return g;
}

// ---------------------------------------------------------------------------
// ruin language
// ---------------------------------------------------------------------------

/** Dark burn disc on the ground. */
export function scorch(r: number): THREE.Mesh {
  const m = mat(KIT.scorch); m.transparent = true; m.opacity = 0.85;
  return at(new THREE.Mesh(new THREE.CircleGeometry(r, 18), m), 0, 0.004, 0, -Math.PI / 2);
}
/** Scatter of broken chunks in team + rubble colours. */
export function rubble(t: TeamPalette, rng: KitRng, count: number, radius: number): THREE.Group {
  const g = new THREE.Group();
  const cols = [t.primary, t.accent, KIT.rubbleDark, KIT.concrete];
  for (let i = 0; i < count; i++) {
    const a = rng.range(0, Math.PI * 2), d = rng.range(radius * 0.3, radius);
    const s = rng.range(0.03, 0.08);
    g.add(at(box(s, s * rng.range(0.4, 0.9), s * rng.range(0.6, 1.4), mat(cols[i % cols.length])), Math.cos(a) * d, s * 0.25, Math.sin(a) * d, rng.range(-0.3, 0.3), rng.range(0, Math.PI), rng.range(-0.3, 0.3)));
  }
  return g;
}
/** Exposed structural frame: four posts and top beams (what's left when the walls go). */
export function exposedFrame(w: number, h: number, d: number): THREE.Group {
  const g = new THREE.Group();
  const m = mat(KIT.darkMetal);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) g.add(at(box(0.02, h, 0.02, m), sx * w / 2, h / 2, sz * d / 2));
  g.add(at(box(w, 0.02, 0.02, m), 0, h, -d / 2), at(box(0.02, 0.02, d, m), -w / 2, h, 0));
  return g;
}
/** A bent post lying at an angle. */
export function bentPost(len: number, c: string): THREE.Group {
  return group(at(cyl(0.01, 0.012, len, mat(c), 6), 0, 0.02, 0, 0, 0, 1.4));
}

// ---------------------------------------------------------------------------
// 63.7 (decision 135 B): person-scale detail. At person scale a building's silhouette is its model scaled by its
// function (BUILDING_PERSON_SCALE.u); its doors, windows, floors, stairs, ladders and site furniture are these parts,
// in person units at 63.2's REFERENCE, laid on the model through pAt (which cancels the model's scale). Classic
// (u = 0) never builds them, so today's models stay exactly as they were.
// ---------------------------------------------------------------------------

/**
 * 67.36: where a windsock stands (on the ground) or a pennant flies (a mast's tip) — an empty point the scene's
 * WindProps finds (rendering/windView.ts). Not a mesh, so it costs nothing and the bake leaves it be; hang it off a
 * group, never a mesh (a baked mesh takes its children with it).
 */
export function windMount(kind: 'sock' | 'pennant', x: number, y: number, z: number): THREE.Object3D {
  const o = new THREE.Object3D();
  o.position.set(x, y, z);
  o.userData.windMount = kind;
  return o;
}

/** How a builder builds: u = 0 is classic (today's model); u > 0 is person scale, one model unit becoming u person units. */
export interface Detail { u: number }
export const CLASSIC_DETAIL: Detail = { u: 0 };
/** Floor to floor, person units (≈ 1.6 people; decision 135). */
export const FLOOR = 2.9;

/** Person-scale parts at a model-space anchor, turned `ry` about y: the group cancels the model's scale, so its children are in person units. */
export function pAt(d: Detail, x: number, y: number, z: number, ry: number, ...parts: THREE.Object3D[]): THREE.Group {
  const g = at(new THREE.Group(), x, y, z, 0, ry, 0);
  g.scale.setScalar(1 / d.u);
  g.add(...parts);
  return g;
}
/** Person units as model units. */
export const pm = (d: Detail, len: number) => len / d.u;
/** Marks a part with the reference measure it is built to (the tests find them by it). */
const tag = <T extends THREE.Object3D>(o: T, ref: string): T => { o.userData.ref = ref; return o; };
/** Marks loose site furniture (lamps, panels, signs, cargo, sandbags): not the building, so not in its size. */
const furniture = (g: THREE.Group): THREE.Group => { g.userData.furniture = true; return g; };

/** A person door (REFERENCE.door, or the stoop door) in its frame, a lamp above; facing +z, its sill at y 0. Ruined: the leaf blown out, lying ahead. */
export function pDoor(t: TeamPalette, ruined: boolean, kind: 'door' | 'stoopDoor' = 'door'): THREE.Group {
  const { width: w, height: h } = REFERENCE[kind], f = 0.12;
  const g = group(at(box(w + 2 * f, h + f, 0.2, mat(t.accent)), 0, (h + f) / 2, -0.06));
  g.add(tag(at(box(w, h, 0.05, ruined ? mat(KIT.glassDark) : mat(t.detail)), 0, h / 2, 0.06), kind));
  if (ruined) g.add(at(box(w, h, 0.05, mat(t.detail)), 0.3, 0.03, h / 2 + 0.5, -Math.PI / 2 + 0.06, 0.4, 0));
  else g.add(at(box(0.05, 0.16, 0.05, mat(t.accent)), w / 2 - 0.13, 1.0, 0.1), at(box(0.32, 0.1, 0.14, matE(t.visor)), 0, h + f + 0.16, 0.06));
  return g;
}
/** A window 1.2 wide, 1.1 tall, framed with a sill: lit glass, dark and cracked when ruined. Facing +z, centred on its middle. */
export function pWindow(t: TeamPalette, ruined: boolean, w = 1.2, h = 1.1): THREE.Group {
  return group(
    at(box(w + 0.16, h + 0.16, 0.12, mat(t.accent)), 0, 0, -0.03),
    tag(at(box(w, h, 0.06, ruined ? mat(KIT.glassDark) : matE(t.visor)), 0, 0, 0.03), 'window'),
    at(box(w + 0.24, 0.08, 0.2, mat(t.accent)), 0, -h / 2 - 0.1, 0.05),
    ...(ruined ? [at(box(w * 0.7, 0.03, 0.08, mat(KIT.lightMetal)), 0, 0, 0.04, 0, 0, 0.5)] : []),
  );
}
/** A facade's windows, `length` wide (centred, facing +z): a row per floor (sill 0.9 above it), every `pitch`; the ground floor leaves room for a door at `doorX`, every floor for a ladder at `gapX`. */
export function pWindowRows(t: TeamPalette, ruined: boolean, length: number, floors: number, opts: { pitch?: number; doorX?: number; gapX?: number; from?: number } = {}): THREE.Group {
  const pitch = opts.pitch ?? 2.6, n = Math.max(1, Math.floor((length - 0.6) / pitch)), g = new THREE.Group();
  for (let f = opts.from ?? 0; f < floors; f++) for (let i = 0; i < n; i++) {
    const x = (i - (n - 1) / 2) * pitch;
    if (f === 0 && opts.doorX !== undefined && Math.abs(x - opts.doorX) < 1.4) continue;   // a door's frame and a window's, apart
    if (opts.gapX !== undefined && Math.abs(x - opts.gapX) < 1.0) continue;               // a ladder up the facade
    g.add(at(pWindow(t, ruined), x, f * FLOOR + 1.45, 0));
  }
  return g;
}
/** The floors read from outside: a slab edge round a box body `w` × `d` (centred) at each floor above the ground. */
export function pFloorBands(t: TeamPalette, w: number, d: number, floors: number): THREE.Group {
  const g = new THREE.Group(), m = mat(t.accent);
  for (let f = 1; f < floors; f++) {
    const y = f * FLOOR;
    g.add(at(box(w + 0.16, 0.2, 0.08, m), 0, y, d / 2 + 0.04), at(box(w + 0.16, 0.2, 0.08, m), 0, y, -d / 2 - 0.04));
    g.add(at(box(0.08, 0.2, d, m), w / 2 + 0.04, y, 0), at(box(0.08, 0.2, d, m), -w / 2 - 0.04, y, 0));
  }
  return g;
}
/** A flight of steps (REFERENCE.step) climbing `rise` to its top at z = 0, coming down toward +z; `width` wide, rails when it is more than two steps. */
export function pStairs(rise: number, width = 1.3): THREE.Group {
  const { rise: r, tread } = REFERENCE.step, n = Math.max(1, Math.round(rise / r)), rr = rise / n, g = new THREE.Group(), m = mat(KIT.concrete);
  for (let i = 0; i < n; i++) g.add(tag(at(box(width, rr * (i + 1), tread, m), 0, rr * (i + 1) / 2, (n - i - 0.5) * tread), 'step'));
  if (n > 2) for (const sx of [-1, 1]) {
    const run = n * tread, len = Math.hypot(run, rise);
    g.add(at(box(0.05, 0.05, len, mat(KIT.darkMetal)), sx * (width / 2 - 0.04), rise / 2 + 0.9, run / 2, Math.atan2(rise, run)));
    for (const k of [0.1, 0.9]) g.add(at(box(0.05, 0.9, 0.05, mat(KIT.darkMetal)), sx * (width / 2 - 0.04), rise * (1 - k) + 0.45, run * k));
  }
  return g;
}
/** A ladder (REFERENCE.ladder) `h` tall against a wall behind it (−z), its rails running on 1.0 above the top to hold. */
export function pLadder(h: number): THREE.Group {
  const { width: w, rung } = REFERENCE.ladder, m = mat(KIT.lightMetal);
  const g = group(at(box(0.05, h + 1.0, 0.05, m), -w / 2, (h + 1.0) / 2, 0.16), at(box(0.05, h + 1.0, 0.05, m), w / 2, (h + 1.0) / 2, 0.16));
  for (let y = rung; y < h + 0.01; y += rung) g.add(tag(at(box(w, 0.035, 0.035, m), 0, y, 0.16), 'rung'));
  return g;
}
/** A guard rail `len` long along x, 1.0 tall: posts every 1.5 or so, a top and a mid rail. */
export function pRail(len: number): THREE.Group {
  const m = mat(KIT.darkMetal), n = Math.max(1, Math.round(len / 1.5)), g = group(at(box(len, 0.05, 0.05, m), 0, 1.0, 0), at(box(len, 0.04, 0.04, m), 0, 0.5, 0));
  for (let i = 0; i <= n; i++) g.add(at(box(0.05, 1.0, 0.05, m), -len / 2 + (i / n) * len, 0.5, 0));
  return g;
}
/** A rail round the edge of a roof `w` × `d` (centred). */
export function pRoofRail(w: number, d: number): THREE.Group {
  return group(at(pRail(w), 0, 0, d / 2 - 0.1), at(pRail(w), 0, 0, -d / 2 + 0.1), at(pRail(d), w / 2 - 0.1, 0, 0, 0, Math.PI / 2, 0), at(pRail(d), -w / 2 + 0.1, 0, 0, 0, Math.PI / 2, 0));
}
/** An entry porch for a curved wall (a dome, a hull): a box `w` wide, `h` tall, reaching `d` out, its door on the +z face. */
export function pVestibule(t: TeamPalette, ruined: boolean, w = 2.2, h = 2.9, d = 2.4): THREE.Group {
  return group(at(box(w, h, d, mat(t.primary)), 0, h / 2, -d / 2 + 0.6), at(box(w + 0.2, 0.18, d + 0.2, mat(t.accent)), 0, h, -d / 2 + 0.6), at(pDoor(t, ruined), 0, 0, 0.61));
}
/** A barrel, REFERENCE-sized: 0.58 across, 0.88 tall, two hoops. */
export function pBarrel(c: string): THREE.Group {
  const r = 0.29, h = 0.88;
  return furniture(group(tag(at(cyl(r, r, h, mat(c), 12), 0, h / 2, 0), 'barrel'), at(torus(r, 0.025, mat(KIT.darkMetal), 6, 16), 0, h * 0.3, 0, Math.PI / 2), at(torus(r, 0.025, mat(KIT.darkMetal), 6, 16), 0, h * 0.7, 0, Math.PI / 2)));
}
/** A crate at one of REFERENCE.crate's sizes. */
export function pCrate(t: TeamPalette, s: number = REFERENCE.crate.standard): THREE.Group {
  return furniture(group(tag(at(box(s, s, s, mat(t.accent)), 0, s / 2, 0), 'crate'), at(box(s * 1.02, 0.05, 0.05, mat(KIT.darkMetal)), 0, s / 2, 0), at(box(0.05, s * 1.02, 0.05, mat(KIT.darkMetal)), 0, s / 2, s / 2)));
}
/** A pallet, 1.2 × 1.0, 0.14 tall. */
export function pPallet(): THREE.Group {
  const g = new THREE.Group();
  for (let i = -1; i <= 1; i++) g.add(at(box(1.2, 0.04, 0.16, mat('#9a8460')), 0, 0.12, i * 0.42));
  for (const x of [-0.55, 0, 0.55]) g.add(at(box(0.12, 0.1, 1.0, mat('#86704f')), x, 0.05, 0));
  return furniture(g);
}
/** A bench, 1.8 long, its seat at 0.45. */
export function pBench(): THREE.Group {
  return furniture(group(at(box(1.8, 0.06, 0.45, mat('#9a8460')), 0, 0.45, 0), at(box(0.08, 0.45, 0.4, mat(KIT.darkMetal)), -0.75, 0.22, 0), at(box(0.08, 0.45, 0.4, mat(KIT.darkMetal)), 0.75, 0.22, 0)));
}
/** A site light, `h` tall; the head glows when intact, the pole leans over when ruined. */
export function pLightPole(t: TeamPalette, ruined: boolean, h = 6): THREE.Group {
  const g = group(at(cyl(0.07, 0.09, h, mat(KIT.darkMetal), 6), 0, h / 2, 0), at(box(0.7, 0.18, 0.4, mat(t.accent)), 0.25, h + 0.05, 0), at(box(0.55, 0.05, 0.3, ruined ? mat(KIT.glassDark) : matE(t.visor)), 0.25, h - 0.06, 0));
  if (ruined) g.rotation.z = 0.75;
  return furniture(g);
}
/** The team's sign: a 1.6 × 0.8 board at 2.2 on two posts; knocked flat when ruined. */
export function pSign(t: TeamPalette, ruined: boolean): THREE.Group {
  const g = group(at(cyl(0.05, 0.05, 2.6, mat(KIT.darkMetal), 6), -0.6, 1.3, 0), at(cyl(0.05, 0.05, 2.6, mat(KIT.darkMetal), 6), 0.6, 1.3, 0),
    at(box(1.6, 0.8, 0.06, mat(t.primary)), 0, 2.2, 0.05), at(box(1.6, 0.16, 0.07, mat(t.detail)), 0, 1.86, 0.05), at(box(1.0, 0.14, 0.08, mat(t.visor)), 0, 2.4, 0.05));
  if (ruined) g.rotation.x = -1.3;
  return furniture(g);
}
/** A row of `n` sandbags (REFERENCE.sandbag) along x, `high` courses, laid in bond. */
export function pSandbags(n: number, high = 1): THREE.Group {
  const s = REFERENCE.sandbag, g = new THREE.Group(), m = mat('#a89a78');
  for (let k = 0; k < high; k++) for (let i = 0; i < n - (k % 2); i++) g.add(tag(at(box(s.width, s.height, s.depth, m), (i - (n - 1) / 2 + (k % 2) * 0.5) * s.width * 1.02, s.height * (k + 0.5), 0), 'sandbag'));
  return furniture(g);
}
/** A bollard, 1.0 tall. */
export function pBollard(t: TeamPalette): THREE.Group {
  return furniture(group(at(cyl(0.12, 0.13, 1.0, mat(t.detail), 8), 0, 0.5, 0), at(cyl(0.135, 0.135, 0.1, mat(t.accent), 8), 0, 0.85, 0)));
}
/** Two rope posts 0.95 tall, `gap` apart, a rope between. */
export function pRopePosts(t: TeamPalette, gap = 2.4): THREE.Group {
  return furniture(group(at(cyl(0.05, 0.06, 0.95, mat(KIT.lightMetal), 6), -gap / 2, 0.47, 0), at(cyl(0.05, 0.06, 0.95, mat(KIT.lightMetal), 6), gap / 2, 0.47, 0), at(box(gap, 0.04, 0.04, mat(t.detail)), 0, 0.85, 0)));
}
/** A solar panel 2.0 × 1.0 on a post, its low edge at 0.9, tilted toward +z; knocked down when ruined. */
export function pSolar(t: TeamPalette, ruined: boolean): THREE.Group {
  const panel = group(at(box(2.0, 0.06, 1.0, mat(t.accent)), 0, 0, 0), at(box(1.9, 0.03, 0.9, mat(KIT.solar)), 0, 0.04, 0));
  const post = at(cyl(0.06, 0.07, 1.2, mat(KIT.darkMetal), 6), 0, 0.6, 0);
  if (ruined) { at(post, 0.3, 0.1, 0, 0, 0, 1.3); at(panel, 0.9, 0.12, 0.3, 0.2, 0.4, 0.3); } else at(panel, 0, 1.3, 0, -0.45);
  return furniture(group(post, panel));
}
